import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ListPlus, Lock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { usePlan } from '../hooks/usePlan';
import { listProjects, type Project } from '../services/projects';
import Layout from '../components/Layout';
import ProjectCard from '../components/ProjectCard';
import { Skeleton } from '../components/ui/Skeleton';
import { ErrorAlert } from '../components/ui/ErrorAlert';
import { EmptyState } from '../components/ui/EmptyState';
import { trackEvent } from '../firebase';
import * as Sentry from '@sentry/react';

export default function Dashboard() {
  const { user } = useAuth();
  const { limits } = usePlan();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await listProjects(user.uid);
      setProjects(data);
    } catch (err: unknown) {
      Sentry.captureException(err);
      setError((err as { message?: string }).message ?? 'Failed to load projects.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [user]);

  const atProjectLimit = !loading && projects.length >= limits.maxProjects;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 m-0">My Waitlists</h2>
          <div className="flex items-center gap-3">
            {atProjectLimit ? (
              <Link
                to="/profile"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-semibold text-sm transition-colors"
                onClick={() => trackEvent('upgrade_cta_clicked', { source: 'new_waitlist_button' })}
              >
                <Lock size={14} />
                Upgrade for more waitlists
              </Link>
            ) : (
              <Link
                to="/projects/new"
                className="inline-flex items-center px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-semibold text-sm transition-colors"
                onClick={() => trackEvent('new_waitlist_clicked')}
              >
                + New Waitlist
              </Link>
            )}
          </div>
        </div>

        {/* Free-tier project limit banner */}
        {atProjectLimit && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6">
            <Lock size={16} className="text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800 m-0">
                Free plan: 1 waitlist included
              </p>
              <p className="text-xs text-amber-700 m-0 mt-0.5">
                You&apos;ve used your free waitlist.{' '}
                <Link to="/profile" className="underline font-medium hover:text-amber-900">
                  Upgrade to Pro
                </Link>{' '}
                for unlimited waitlists.
              </p>
            </div>
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-5 shadow-sm animate-pulse flex flex-col gap-3"
              >
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <div className="flex gap-2 mt-1">
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-24" />
                </div>
                <div className="flex gap-2 mt-2">
                  <Skeleton className="h-8 flex-1" />
                </div>
              </div>
            ))}
          </div>
        )}
        {error && <ErrorAlert label="Could not load waitlists" message={error} />}

        {!loading && projects.length === 0 && (
          <EmptyState
            icon={ListPlus}
            heading="No waitlists yet"
            body="Get started in minutes — create your first waitlist and drop one script tag into your site."
            action={{ label: 'Create your first waitlist', to: '/projects/new' }}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      </div>
    </Layout>
  );
}
